// src/pages/vendors/po-transactions-components/POTransactionTable.jsx
import { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Button, Menu, MenuItem,
  ListItemIcon, ListItemText, CircularProgress, alpha, useTheme, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Collapse, Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';

const TYPE_COLORS = {
  products:  '#1565c0', chemicals: '#7b1fa2', caps: '#00796b',
  bottles:   '#2e7d32', labels:    '#e65100', supplies: '#5d4037',
};

const PACKAGING_TYPES = new Set(['caps', 'bottles', 'labels', 'supplies']);

// Sort order: products first, chemicals second, then packaging types together
const TYPE_ORDER = { products: 0, chemicals: 1, caps: 2, bottles: 3, labels: 4, supplies: 5 };
function sortItems(items) {
  return [...items].sort((a, b) => (TYPE_ORDER[a._type] ?? 99) - (TYPE_ORDER[b._type] ?? 99));
}

function getItemName(tx) {
  return tx.products?.name || tx.raw_materials?.name || tx.caps?.name ||
         tx.bottles?.name  || tx.labels?.name        || tx.supplies?.name || '—';
}

function getQty(tx) {
  const raw = tx._type === 'chemicals' ? (tx.unit_quantity ?? tx.total_quantity ?? 0)
            : tx._type === 'products'  ? (tx.total_base_units ?? tx.quantity_change ?? 0)
            : (tx.quantity ?? 0);
  return (parseFloat(raw) || 0).toLocaleString();
}

function fmtDate(d) {
  if (!d) return '—';
  const p = d.split('T')[0].split('-');
  return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : d;
}

function TypeChip({ type }) {
  const color = TYPE_COLORS[type] || '#666';
  return (
    <Chip label={type.charAt(0).toUpperCase() + type.slice(1)} size="small"
      sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 500, fontSize: '0.72rem', border: `1px solid ${alpha(color, 0.3)}` }} />
  );
}

function StatusChip({ status }) {
  return status === 'delivered'
    ? <Chip label="Delivered" size="small" color="success" variant="outlined" icon={<CheckIcon sx={{ fontSize: 13 }} />} sx={{ fontWeight: 500, fontSize: '0.72rem' }} />
    : <Chip label="Pending"   size="small" color="warning" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.72rem' }} />;
}

const hSx = (theme) => ({
  fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white',
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  color: 'primary.main', py: 1.5, px: 2,
});
const cSx  = { py: 1.5, px: 2 };
const subSx = { py: 1, px: 2, bgcolor: alpha('#f5f5f5', 0.4) };

// ── Actions menu shared between group rows and sub-rows ───────────────────────

function ActionsMenu({ tx, onDelete, onEdit, theme }) {
  const [anchor, setAnchor] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  const close = () => setAnchor(null);

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(tx); } finally { setDeleting(false); setConfirmDel(false); }
  };

  return (
    <>
      <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'scale(1.1)' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`, minWidth: 210 } } }}>
        {onEdit && (
          <MenuItem onClick={() => { onEdit(tx); close(); }}
            sx={{ borderRadius: 1, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Edit Transaction</ListItemText>
          </MenuItem>
        )}
        {onDelete && [
          <Divider key="d" sx={{ my: 1 }} />,
          <MenuItem key="del" onClick={() => { setConfirmDel(true); close(); }}
            sx={{ borderRadius: 1, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        ]}
      </Menu>

      <Dialog open={confirmDel} onClose={() => !deleting && setConfirmDel(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 500 }}>Delete Transaction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete the <strong>{getItemName(tx)}</strong> purchase transaction? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDel(false)} disabled={deleting} sx={{ borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained" color="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function GroupDeleteButton({ group, onDelete, theme }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting]       = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(group.items.map(tx => onDelete(tx)));
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <IconButton size="small" onClick={e => { e.stopPropagation(); setConfirmOpen(true); }}
        sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`, color: 'error.main', ml: 0.5,
          '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), border: `1px solid ${theme.palette.error.main}` } }}>
        <DeleteIcon fontSize="small" />
      </IconButton>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 500 }}>Delete All Transactions?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete all <strong>{group.items.length} transactions</strong> in PO <strong>{group.po_number}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting} sx={{ borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained" color="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function POTransactionTable({
  transactions = [],
  loading = false,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  onConfirmGroup,
  onEdit,
  onDelete,
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpand = (key) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Group by po_number; ungrouped stay individual
  const displayRows = useMemo(() => {
    const groups = new Map();
    const ungrouped = [];

    for (const tx of transactions) {
      if (tx.po_number) {
        if (!groups.has(tx.po_number)) {
          groups.set(tx.po_number, { _isGroup: true, po_number: tx.po_number, items: [], created_at: tx.created_at });
        }
        groups.get(tx.po_number).items.push(tx);
      } else {
        ungrouped.push({ _isGroup: false, tx, created_at: tx.created_at });
      }
    }

    const groupRows = [...groups.values()].map(g => ({
      ...g,
      delivery_status: g.items.every(t => t.delivery_status === 'delivered') ? 'delivered' : 'ordered',
      expected_delivery_date: g.items[0]?.expected_delivery_date,
      location: g.items[0]?.location || g.items[0]?.to_location,
    }));

    return [...groupRows, ...ungrouped]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [transactions]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={48} /></Box>;
  }

  if (transactions.length === 0) {
    return (
      <Paper sx={{ textAlign: 'center', py: 8, px: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ p: 2.5, borderRadius: 3, mb: 3, width: 'fit-content', mx: 'auto', background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
          <AssignmentIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
        </Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>No PO Transactions Found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Create one using the button above</Typography>
      </Paper>
    );
  }

  const visibleRows = displayRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper sx={{
      borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`, overflow: 'hidden',
      '&:hover': { boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hSx(theme), width: '4%' }} />
              <TableCell sx={{ ...hSx(theme), width: '13%' }}>PO Number</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '18%' }}>Type / Item</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '8%'  }}>Qty</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '13%' }}>Location</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '14%' }}>Expected Delivery</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '13%' }}>Status</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '9%'  }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map(row => {
              if (row._isGroup) {
                const isOpen = expanded.has(row.po_number);
                const pendingItems = row.items.filter(t => t.delivery_status !== 'delivered');
                return [
                  // ── Group header row ──
                  <TableRow key={`grp-${row.po_number}`}
                    onClick={() => toggleExpand(row.po_number)}
                    sx={{ cursor: 'pointer', bgcolor: alpha(theme.palette.primary.main, 0.02), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) }, transition: 'background 0.15s' }}>
                    <TableCell sx={{ ...cSx, py: 1.5 }}>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        {isOpen ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.po_number}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.items.length} item{row.items.length !== 1 ? 's' : ''}</Typography>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                        {[...new Set(row.items.map(t => t._type))].map(type => (
                          <TypeChip key={type} type={type} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.items.reduce((sum, t) => {
                          const q = t._type === 'chemicals' ? (t.unit_quantity ?? t.total_quantity ?? 0)
                                  : t._type === 'products'  ? (t.total_base_units ?? t.quantity_change ?? 0)
                                  : (t.quantity ?? 0);
                          return sum + (parseFloat(q) || 0);
                        }, 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.location?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Typography variant="body2" color="text.secondary">{fmtDate(row.expected_delivery_date)}</Typography>
                    </TableCell>
                    <TableCell sx={cSx}><StatusChip status={row.delivery_status} /></TableCell>
                    <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {pendingItems.length > 0 && onConfirmGroup && (
                          <Button size="small" variant="outlined" color="success"
                            onClick={() => onConfirmGroup(pendingItems)}
                            sx={{ borderRadius: 2, fontSize: '0.75rem', fontWeight: 500, py: 0.5, px: 1.5 }}>
                            Confirm PO
                          </Button>
                        )}
                        {onDelete && (
                          <GroupDeleteButton group={row} onDelete={onDelete} theme={theme} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>,

                  // ── Expanded sub-rows ──
                  <TableRow key={`grp-exp-${row.po_number}`}>
                    <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                      <Collapse in={isOpen} unmountOnExit>
                        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableBody>
                            {(() => {
                              // Merge rows with same type+item — sum qty, pending wins over delivered
                              const getRawQty = (t) => t._type === 'chemicals' ? (t.unit_quantity ?? t.total_quantity ?? 0)
                                : t._type === 'products' ? (t.total_base_units ?? t.quantity_change ?? 0)
                                : (t.quantity ?? 0);
                              const merged = new Map();
                              for (const tx of row.items) {
                                const key = `${tx._type}::${getItemName(tx)}`;
                                const qty = parseFloat(getRawQty(tx)) || 0;
                                if (!merged.has(key)) {
                                  merged.set(key, { ...tx, _totalQty: qty });
                                } else {
                                  const m = merged.get(key);
                                  m._totalQty += qty;
                                  if (tx.delivery_status !== 'delivered') m.delivery_status = tx.delivery_status;
                                }
                              }
                              return sortItems([...merged.values()]).map((tx, idx, arr) => {
                              const isFirstPackaging = PACKAGING_TYPES.has(tx._type) && (idx === 0 || !PACKAGING_TYPES.has(arr[idx - 1]._type));
                              return [
                              isFirstPackaging && (
                                <TableRow key={`pkg-divider-${row.po_number}`}>
                                  <TableCell colSpan={8} sx={{ py: 0.5, px: 2, border: 0, bgcolor: alpha(theme.palette.grey[300], 0.3) }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                                      Packaging
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ),
                              <TableRow key={`${tx._type}-${getItemName(tx)}`} sx={{ '&:hover': { bgcolor: alpha(theme.palette.grey[300], 0.3) } }}>
                                <TableCell sx={{ ...subSx, width: '4%', borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }} />
                                <TableCell sx={{ ...subSx, width: '13%' }}>
                                  <Typography variant="caption" color="text.secondary">└</Typography>
                                </TableCell>
                                <TableCell sx={{ ...subSx, width: '18%' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TypeChip type={tx._type} />
                                    <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {getItemName(tx)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ ...subSx, width: '8%' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{(tx._totalQty != null ? tx._totalQty : (parseFloat(getQty(tx)) || 0)).toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell sx={{ ...subSx, width: '13%' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {tx.location?.name || tx.to_location?.name || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ ...subSx, width: '14%' }}>
                                  <Typography variant="body2" color="text.secondary">{fmtDate(tx.expected_delivery_date)}</Typography>
                                </TableCell>
                                <TableCell sx={{ ...subSx, width: '13%' }}><StatusChip status={tx.delivery_status} /></TableCell>
                                <TableCell sx={{ ...subSx, width: '9%' }} />
                              </TableRow>
                              ];
                              });
                            })()}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                ];
              }

              // ── Ungrouped row (no PO number) ──
              const { tx } = row;
              return (
                <TableRow key={`single-${tx.id}`} hover sx={{ cursor: 'default', '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell sx={cSx} />
                  <TableCell sx={cSx}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>—</Typography>
                  </TableCell>
                  <TableCell sx={cSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TypeChip type={tx._type} />
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getItemName(tx)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={cSx}><Typography variant="body2" sx={{ fontWeight: 500 }}>{getQty(tx)}</Typography></TableCell>
                  <TableCell sx={cSx}><Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.location?.name || tx.to_location?.name || '—'}</Typography></TableCell>
                  <TableCell sx={cSx}><Typography variant="body2" color="text.secondary">{fmtDate(tx.expected_delivery_date)}</Typography></TableCell>
                  <TableCell sx={cSx}><StatusChip status={tx.delivery_status} /></TableCell>
                  <TableCell sx={cSx} align="right">
                    <ActionsMenu tx={tx} onEdit={onEdit} onDelete={onDelete} theme={theme} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`, borderRadius: '0 0 12px 12px' }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]} component="div"
          count={displayRows.length} rowsPerPage={rowsPerPage} page={page}
          onPageChange={onPageChange} onRowsPerPageChange={onRowsPerPageChange}
          sx={{ px: 3, py: 1, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.9rem', fontWeight: 500 } }}
        />
      </Box>
    </Paper>
  );
}
